import { Autocomplete, createFilterOptions, Paper, TextField } from "@mui/material";
import React, { HTMLAttributes, ReactNode, useMemo, useState } from "react";
import { getAutocompleteSx } from "../../util/style";
import { Tooltip } from "./Tooltip";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";

type PaperProps = Omit<HTMLAttributes<HTMLElement>, "children"> & {
    children?: ReactNode;
};

type AutocompleteFieldProps<Value> = {
    label: string;
    name: string;
    options: ReadonlyArray<Value>;
    handleValueChange?: (value: any, field?: string) => void,
    handleValidation?: (value: any, field?: string) => void | boolean,
    defaultValue?: Value | Value[];
    error: string | undefined;
    onChange?: (value: Value) => void;
    getOptionLabel?: (option: Value) => string;
    isOptionEqualToValue?: (option: Value, value: Value) => boolean;
    multiple?: boolean;
    info?: string;
    className?: string;
    disabled?: boolean;
}

export default function AutocompleteField<Value>({
                                                     label,
                                                     name,
                                                     options,
                                                     handleValueChange = () => {
                                                     },
                                                     handleValidation = () => {
                                                     },
                                                     defaultValue,
                                                     error,
                                                     getOptionLabel,
                                                     isOptionEqualToValue,
                                                     multiple = false,
                                                     info,
                                                     className,
                                                     disabled = false,
                                                 }: AutocompleteFieldProps<Value>) {
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState<string>("");
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const isMobile = useMediaQuery({ maxWidth: 767 });

    const filterOptions = useMemo(
      () => createFilterOptions<Value>({ matchFrom: 'start', ignoreAccents: false, stringify: getOptionLabel }),
      [getOptionLabel]
    );

    const handleOnChange = (value: Value | Value[] | null) => {
        handleValueChange(value, name);
        handleValidation(value as any, name);
    };

    return (
      <div className={className}>
          <Autocomplete
            multiple={multiple}
            options={options}
            disabled={disabled}
            value={defaultValue ?? null}
            inputValue={inputValue}
            fullWidth
            onInputChange={(_, newValue) => setInputValue(newValue)}
            filterOptions={filterOptions}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            noOptionsText={t("common.autocomplete-empty")}
            sx={getAutocompleteSx(name, error, isMobile ? "sm" : "")}
            disableClearable={!isFocused}
            onChange={(_, value) => handleOnChange(value)}
            ListboxProps={{
                style: {
                    maxHeight: 250,
                    overflow: 'auto',
                },
                sx: {
                    maxHeight: 250,
                    overflow: 'auto',

                    '& .MuiAutocomplete-option[aria-selected="true"]': {
                        backgroundColor: '#e6f4ea',
                        color: '#52ae30',
                        fontWeight: 'bold',
                    },

                    '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
                        backgroundColor: '#e6f4ea',
                        color: '#52ae30',
                    },

                    '& .MuiAutocomplete-option.Mui-focused:not([aria-selected="true"])': {
                        backgroundColor: '#f0f0f0',
                    },
                }
            }}


            PaperComponent={(props) => (
              <Paper
                {...(props as PaperProps)}
                elevation={3}
                sx={{
                    minWidth: 190,
                    marginTop: '4px',
                    borderRadius: '8px',
                }}
              />
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label={label}
                variant="outlined"
                error={!!error}
                helperText={error}
                InputLabelProps={{
                    sx: {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: !!info ? 'calc(100% - 80px)' : 'calc(100% - 40px)',

                        '&.MuiInputLabel-shrink': {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '120%',
                        },
                        '& .MuiInputLabel-root': {
                            fontSize: '0.5rem',
                        },
                    },
                    required: false
                }}
                InputProps={
                    {
                        ...params.InputProps,
                        endAdornment:
                          <>
                              {!!info && <Tooltip text={info}/>}
                              {params.InputProps.endAdornment}
                          </>
                    }}
              />
            )}
          />
      </div>
    )
}
